import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'motion/react';
import type { ComponentProps, ReactNode } from 'react';
import Loader2Icon from '@/components/icons/Loader2Icon';
import { useUiKitTranslation } from '@/ui-kit/localization/UiKitLocalizationContext';
import { cn } from '@/ui-kit/utils';

const buttonVariants = cva(
  'flex w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-surface-action-primary text-text-inverse [&_svg]:text-icon-inverse',
        secondary: 'text-text-default [&_svg]:text-icon-default bg-surface-action-secondary',
        tertiary: 'text-text-default [&_svg]:text-icon-default underline',
        destructive: 'bg-surface-error text-text-error [&_svg]:text-icon-error',
        // px-[19px] is the regular padding (20px) - border width (1px)
        pill: 'bg-surface-action-tertiary text-caption-semibold border-border-dark h-9 w-fit border px-[19px]',
      },
      size: {
        md: null,
        sm: null,
      },
      disabled: {
        false: null,
        true: 'pointer-events-none cursor-default',
      },
      loading: {
        false: null,
        true: 'pointer-events-none cursor-default',
      },
    },
    compoundVariants: [
      {
        variant: ['primary', 'secondary', 'tertiary', 'destructive'],
        class: 'px-4',
      },
      // Size
      {
        variant: ['primary', 'secondary', 'tertiary', 'destructive'],
        size: 'md',
        class: 'text-body-semibold h-13 [&_svg]:size-6',
      },
      {
        variant: ['primary', 'secondary', 'tertiary'],
        size: 'sm',
        class: 'text-caption-semibold h-10 [&_svg]:size-4.5',
      },
      // Disabled
      {
        variant: ['primary', 'secondary', 'destructive'],
        disabled: true,
        loading: false,
        class: 'bg-surface-disabled text-text-disabled [&_svg]:text-icon-disabled',
      },
      {
        variant: ['tertiary'],
        disabled: true,
        loading: false,
        class: 'text-text-disabled [&_svg]:text-icon-disabled',
      },
      {
        variant: ['pill'],
        disabled: true,
        loading: false,
        class: 'border-border-disabled text-text-disabled [&_svg]:text-icon-disabled',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      disabled: false,
      loading: false,
    },
  },
);

// Ensure the "size" prop is only allowed for sizable variants
type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;
type SizableVariant = 'primary' | 'secondary' | 'tertiary';
type NonSizableVariant = Exclude<ButtonVariant, SizableVariant>;

type BaseButtonVariantProps = Omit<VariantProps<typeof buttonVariants>, 'variant' | 'size'> & {
  asChild?: boolean;
  children: ReactNode;
};

export type ButtonVariantProps =
  | (BaseButtonVariantProps & { variant?: SizableVariant | undefined; size?: ButtonSize })
  | (BaseButtonVariantProps & { variant: NonSizableVariant; size?: never });

export type ButtonProps = Omit<ComponentProps<typeof motion.button>, 'children'> & ButtonVariantProps;

const MotionSlot = motion.create(Slot);

const Button = ({
  className,
  disabled,
  variant,
  size,
  loading,
  type = 'button',
  asChild = false,
  children,
  ...props
}: ButtonProps) => {
  const translation = useUiKitTranslation();
  const Component = asChild ? MotionSlot : motion.button;

  const buttonSpecificProps = {
    type,
    disabled: !!(disabled ?? loading),
  };

  const buttonContent = asChild ? (
    children
  ) : (
    // Wrap children in a <span> to prevent browser translation tools (e.g. Google Translate) from
    // crashing React when the button swaps to loading state. See AC-415 for details.
    // asChild passes children directly for Radix Slot's prop merging.
    <span className="inline-flex items-center gap-[inherit]">{children}</span>
  );

  return (
    <Component
      className={cn(buttonVariants({ variant, size, disabled, loading, className }))}
      whileTap={{ scale: 0.97, transition: { type: 'spring', duration: 0.3, bounce: 0.5 } }}
      {...(asChild ? {} : buttonSpecificProps)}
      {...props}
    >
      {loading ? (
        <>
          <Loader2Icon className="absolute animate-spin p-0" aria-hidden="true" />
          {/* Keeps the button width the same when loading */}
          <span aria-hidden="true" className="invisible">
            {children}
          </span>
          <span className="sr-only" data-testid="button-loading">
            {translation.loading}
          </span>
        </>
      ) : (
        buttonContent
      )}
    </Component>
  );
};

export default Button;
